import { useActionState } from "react";
import { type PublishState, publishTemplateAction } from "@/actions/templates";
import { Button } from "@/components/Button";
import type { Violation } from "@shared/publishGate";
import styles from "./PublishPanel.module.css";

type PublishPanelProps = {
  templateId: number;
  status: string;
  // The gate run server-side on load, so the page opens already saying what is
  // outstanding rather than waiting for someone to press Publish and fail.
  violations: Violation[];
};

const initial: PublishState = { outcome: "idle" };

const ViolationList = ({ violations }: { violations: Violation[] }) => (
  <ul className={styles.list}>
    {violations.map((violation) => (
      <li
        key={`${violation.rule}-${violation.locale ?? ""}`}
        className={styles.item}
      >
        <span className={styles.rule}>
          {violation.rule}
          {violation.locale === undefined ? null : ` · ${violation.locale}`}
        </span>
        <span className={styles.detail}>{violation.detail}</span>
      </li>
    ))}
  </ul>
);

export const PublishPanel = ({
  templateId,
  status,
  violations,
}: PublishPanelProps) => {
  const [state, formAction, isPending] = useActionState(
    publishTemplateAction.bind(null, templateId),
    initial,
  );

  // The freshly-run gate outranks the one from page load, which is stale the
  // moment anything on this page is saved.
  const outstanding =
    state.outcome === "rejected" ? state.violations : violations;
  const canPublish = outstanding.length === 0;

  return (
    <section className={styles.section}>
      <h2 className={styles.heading}>Publish</h2>
      <p className={styles.lede}>
        Publishing runs every rule that spans tables, then records a revision in
        the same transaction. There is no restore: turning back means editing
        the draft and publishing again.
      </p>

      {state.outcome === "published" ? (
        <output className={styles.published}>
          Published as revision {state.revisionNo}.
        </output>
      ) : null}
      {state.outcome === "error" ? (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      {canPublish ? (
        <p className={styles.ready}>Every publish rule is satisfied.</p>
      ) : (
        <>
          <p className={styles.blocked}>
            {outstanding.length} rule{outstanding.length === 1 ? "" : "s"} not
            satisfied:
          </p>
          <ViolationList violations={outstanding} />
        </>
      )}

      <form action={formAction}>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending
            ? "Publishing..."
            : status === "PUBLISHED"
              ? "Publish again"
              : "Publish"}
        </Button>
      </form>
    </section>
  );
};
